import { companyConfig } from './companyConfig'

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color (e.g., '#b34044')
 * @returns {string} - RGB values (e.g., '179, 64, 68')
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '0, 0, 0'
}

/**
 * Apply theme configuration to CSS variables and Vuetify
 * @param {Object} themeConfig - Theme configuration from company config
 */
export function applyTheme(themeConfig) {
  if (!themeConfig) return

  const root = document.documentElement

  // Apply color variables
  if (themeConfig.colors) {
    if (themeConfig.colors.primary) {
      root.style.setProperty('--brand-primary', themeConfig.colors.primary)
      root.style.setProperty('--color-primary', themeConfig.colors.primary)
      root.style.setProperty('--header-bg', themeConfig.colors.primary)
      root.style.setProperty('--button-primary-bg', themeConfig.colors.primary)
      root.style.setProperty('--progress-active', themeConfig.colors.primary)
      // Add RGB version for rgba() usage in box-shadows, etc.
      root.style.setProperty('--brand-primary-rgb', hexToRgb(themeConfig.colors.primary))
    }

    if (themeConfig.colors.secondary) {
      root.style.setProperty('--brand-secondary', themeConfig.colors.secondary)
      root.style.setProperty('--color-secondary', themeConfig.colors.secondary)
    }

    if (themeConfig.colors.accent) {
      root.style.setProperty('--brand-accent', themeConfig.colors.accent)
      root.style.setProperty('--color-accent', themeConfig.colors.accent)
    }

    if (themeConfig.colors.background) {
      root.style.setProperty('--color-background', themeConfig.colors.background)
    }

    if (themeConfig.colors.surface) {
      root.style.setProperty('--color-surface', themeConfig.colors.surface)
    }

    if (themeConfig.colors.text) {
      root.style.setProperty('--color-text', themeConfig.colors.text)
      root.style.setProperty('--text-primary', themeConfig.colors.text)
    }
  }

  // Apply font variables
  if (themeConfig.fonts) {
    if (themeConfig.fonts.heading) {
      root.style.setProperty('--font-heading', `"${themeConfig.fonts.heading}", serif`)
    }

    if (themeConfig.fonts.body) {
      root.style.setProperty('--font-body', `"${themeConfig.fonts.body}", sans-serif`)
      root.style.setProperty('--font-default', `"${themeConfig.fonts.body}", sans-serif`)
    }

    if (themeConfig.fonts.subheading) {
      root.style.setProperty('--font-subheading', `"${themeConfig.fonts.subheading}", serif`)
    }
  }
}

/**
 * Update Vuetify theme dynamically
 * @param {Object} vuetifyInstance - Vuetify instance
 * @param {Object} themeConfig - Theme configuration
 */
export function updateVuetifyTheme(vuetifyInstance, themeConfig) {
  if (!vuetifyInstance || !themeConfig?.colors) {
    return
  }

  try {
    const theme = vuetifyInstance.theme

    // In Vuetify 3, theme.themes is already the object (not a Ref)
    if (!theme || !theme.themes || !theme.themes.light) {
      return
    }

    const lightTheme = theme.themes.light

    // Update all color properties directly
    if (themeConfig.colors.primary) {
      lightTheme.colors.primary = themeConfig.colors.primary
    }
    if (themeConfig.colors.secondary) {
      lightTheme.colors.secondary = themeConfig.colors.secondary
    }
    if (themeConfig.colors.accent) {
      lightTheme.colors.accent = themeConfig.colors.accent
    }
    if (themeConfig.colors.background) {
      lightTheme.colors.background = themeConfig.colors.background
    }
    if (themeConfig.colors.surface) {
      lightTheme.colors.surface = themeConfig.colors.surface
    }
  } catch (error) {
    console.error('Error updating Vuetify theme:', error)
  }
}

/**
 * Load custom fonts for the theme
 * @param {Object} fonts - Font configuration
 */
export function loadCustomFonts(fonts) {
  if (!fonts) return

  const fontsToLoad = []

  // Load all custom fonts from Google Fonts
  // Note: We always load fonts, even if they're Mission's defaults,
  // because they need to be fetched from Google Fonts
  if (fonts.heading) {
    fontsToLoad.push(fonts.heading)
  }

  if (fonts.body) {
    fontsToLoad.push(fonts.body)
  }

  if (fonts.subheading) {
    fontsToLoad.push(fonts.subheading)
  }

  if (fontsToLoad.length > 0) {
    const WebFont = require('webfontloader')
    WebFont.load({
      google: {
        families: fontsToLoad
      }
    })
  }
}

/**
 * Initialize theme from company config
 * This should be called after company config is loaded
 */
export function initializeTheme(vuetifyInstance) {
  const theme = companyConfig.value?.theme

  if (theme) {
    applyTheme(theme)

    if (vuetifyInstance) {
      updateVuetifyTheme(vuetifyInstance, theme)
    }

    if (theme.fonts) {
      loadCustomFonts(theme.fonts)
    }
  }
}
